import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  alpha,
  useTheme,
} from "@mui/material";
import LockClockIcon from "@mui/icons-material/LockClock";
import AddIcon from "@mui/icons-material/Add";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PaymentsIcon from "@mui/icons-material/Payments";
import DeleteIcon from "@mui/icons-material/Delete";
import StarIcon from "@mui/icons-material/Star";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import CloseIcon from "@mui/icons-material/Close";
import { useGetStripeQuery } from "components/Slices/accountsApiSlice";
import {
  ConnectComponentsProvider,
  ConnectAccountOnboarding,
} from "@stripe/react-connect-js";
import { useStripeConnect } from "hooks/useStripeConnect";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { apiSlice } from "components/Slices/apiSlice";
import { AddBankAccountModal } from "./components/AddBankAccountModal";
import { OnboardingRequiredAlert } from "./components/OnboardingRequiredAlert";
import {
  useDeleteBankAccountMutation,
  useSetDefaultBankAccountMutation,
  useGetOnboardingStatusQuery,
  useGetProfileQuery,
} from "components/Slices/usersApiSlice";

const ONBOARDING_COMPLETE_STATUSES = ["active"];

const maskAccountNumber = (n: string | null) => (n ? `••••••${n}` : "");

export function PayoutForm() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const { data: profile } = useGetProfileQuery();
  const { data: onboardingData, isLoading: isLoadingOnboarding } = useGetOnboardingStatusQuery();
  const { data: stripeAccount, isLoading: isLoadingStripe } = useGetStripeQuery();

  const isUserOnboardingComplete = ONBOARDING_COMPLETE_STATUSES.includes(
    onboardingData?.status?.toLowerCase() || ""
  );

  const {
    isPending = false,
    requiredFields = [],
    onboardingComplete = false,
    stripeId: connectedAccountId = "",
  } = stripeAccount ?? {};

  const externalAccounts = stripeAccount?.externalAccounts as
    | {
        id: string;
        bank_name: string;
        account_type: string;
        last4: string;
        transit_number: string;
        routing_number: string;
        status: string;
        default_for_currency?: boolean;
      }[]
    | undefined;

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");
  const [openCardModal, setOpenCardModal] = useState(false);
  const [openAddBankModal, setOpenAddBankModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const [deleteBankAccount, { isLoading: isDeleting }] = useDeleteBankAccountMutation();
  const [setDefaultBankAccount, { isLoading: isSettingDefault }] = useSetDefaultBankAccountMutation();

  const stripeConnectInstance = useStripeConnect(connectedAccountId);

  const showSnackbar = (message: string, severity: "success" | "error" = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleModalClose = () => {
    setOpenCardModal(false);
    if (onboardingComplete && requiredFields.length === 0) {
      showSnackbar("Bank account added successfully!");
    }
    dispatch(apiSlice.util.invalidateTags([{ type: "Payment", id: connectedAccountId }]));
  };

  const handleAddBankSuccess = () => {
    showSnackbar("Bank account added successfully!");
    dispatch(
      apiSlice.util.invalidateTags([
        { type: "Payment", id: connectedAccountId },
        "BankAccounts",
      ])
    );
  };

  const handleDeleteBankAccount = async (bankId: string, isDefault: boolean) => {
    if (externalAccounts && externalAccounts.length === 1) {
      showSnackbar("Cannot delete your only bank account. Add another first.", "error");
      return;
    }
    if (isDefault) {
      showSnackbar("Cannot delete the default account. Set another as default first.", "error");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this bank account?")) return;

    try {
      await deleteBankAccount(bankId).unwrap();
      showSnackbar("Bank account deleted successfully!");
      dispatch(apiSlice.util.invalidateTags([{ type: "Payment", id: connectedAccountId }]));
    } catch (err: any) {
      showSnackbar(err?.data?.message || "Failed to delete bank account.", "error");
    }
  };

  const handleSetDefault = async (bankId: string) => {
    try {
      await setDefaultBankAccount(bankId).unwrap();
      showSnackbar("Default account updated successfully!");
      dispatch(apiSlice.util.invalidateTags([{ type: "Payment", id: connectedAccountId }]));
    } catch (err: any) {
      showSnackbar(err?.data?.message || "Failed to set default account.", "error");
    }
  };

  useEffect(() => {
    if (openCardModal && loading) {
      const timeout = setTimeout(() => setLoading(false), 3000);
      return () => clearTimeout(timeout);
    }
  }, [openCardModal, loading]);

  const sectionCard = {
    borderRadius: 4,
    bgcolor: "background.paper",
    boxShadow: "0 1px 3px rgba(16,24,64,0.06), 0 4px 16px rgba(16,24,64,0.08)",
    overflow: "hidden",
  };

  if (isLoadingOnboarding || isLoadingStripe) {
    return (
      <Box sx={{ ...sectionCard, p: 5, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!isUserOnboardingComplete) {
    return (
      <Box sx={sectionCard}>
        <Box sx={{ px: 3.5, pt: 3, pb: 2.5, borderBottom: "1px solid", borderColor: "divider" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.08), display: "flex", alignItems: "center", justifyContent: "center" }}>
              <PaymentsIcon sx={{ fontSize: "1rem", color: "primary.main" }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "text.primary" }}>Payout Account</Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>Manage how you get paid.</Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ px: 3.5, py: 3 }}>
          <OnboardingRequiredAlert type="payout" />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={sectionCard}>
      {/* Header */}
      <Box
        sx={{
          px: 3.5,
          pt: 3,
          pb: 2.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PaymentsIcon sx={{ fontSize: "1rem", color: "primary.main" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "text.primary" }}>
              Payout Account
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
              Manage how you get paid.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ px: 3.5, py: 3 }}>
        {externalAccounts && externalAccounts.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography sx={{ fontSize: "0.88rem", color: "text.secondary", mb: 2.5 }}>
              No payout account added yet. You won't be able to receive payouts until you add one.
            </Typography>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={() => setOpenAddBankModal(true)}
              sx={{ fontSize: "0.82rem", fontWeight: 600, borderRadius: "10px" }}
            >
              Add Payout Account
            </Button>
          </Box>
        ) : (
          <Box>
            <Grid container spacing={2} sx={{ mb: 2.5 }}>
              {externalAccounts?.map((account) => {
                const isDefault = account.default_for_currency === true;
                return (
                  <Grid item xs={12} sm={6} key={account.id}>
                    <Box
                      sx={{
                        borderRadius: 3,
                        border: "1px solid",
                        borderColor: isDefault
                          ? alpha(theme.palette.primary.main, 0.4)
                          : "divider",
                        bgcolor: isDefault ? alpha(theme.palette.primary.main, 0.02) : "background.paper",
                        p: 2.5,
                      }}
                    >
                      {/* Bank name + actions */}
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
                        <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "text.primary" }}>
                          {account.bank_name}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          {!isDefault && (
                            <Tooltip title="Set as default">
                              <IconButton
                                size="small"
                                aria-label="Set as default account"
                                onClick={() => handleSetDefault(account.id)}
                                disabled={isSettingDefault}
                                sx={{ color: "text.secondary", "&:hover": { color: "primary.main" } }}
                              >
                                <StarOutlineIcon sx={{ fontSize: "1rem" }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title={isDefault ? "Cannot delete default account" : "Delete account"}>
                            <span>
                              <IconButton
                                size="small"
                                aria-label={isDefault ? "Cannot delete default account" : "Delete account"}
                                onClick={() => handleDeleteBankAccount(account.id, isDefault)}
                                disabled={isDeleting || isDefault}
                                sx={{ color: "error.main", opacity: isDefault ? 0.3 : 0.5, "&:hover": { opacity: 1, bgcolor: alpha(theme.palette.error.main, 0.08) } }}
                              >
                                <DeleteIcon sx={{ fontSize: "1rem" }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </Box>

                      {/* Chips */}
                      <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                        {isDefault && (
                          <Chip
                            icon={<StarIcon sx={{ fontSize: "0.75rem !important" }} />}
                            label="Default"
                            size="small"
                            color="primary"
                            sx={{ height: 22, fontSize: "0.78rem", fontWeight: 600 }}
                          />
                        )}
                        <Chip
                          label={onboardingComplete ? "Verified" : "Pending"}
                          size="small"
                          color={onboardingComplete ? "success" : "warning"}
                          sx={{ height: 22, fontSize: "0.78rem", fontWeight: 600 }}
                        />
                      </Box>

                      {/* Account info */}
                      <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mb: 0.5 }}>
                        {user?.firstName} {user?.lastName}
                      </Typography>
                      <Typography sx={{ fontFamily: "monospace", fontSize: "0.95rem", letterSpacing: 1, mb: 0.5 }}>
                        {maskAccountNumber(account.last4)}
                      </Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mb: !onboardingComplete ? 2 : 0 }}>
                        Routing: •••••{account.routing_number}
                      </Typography>

                      {!onboardingComplete && (
                        isPending && requiredFields.length === 0 ? (
                          <Button
                            startIcon={<LockClockIcon />}
                            variant="outlined"
                            fullWidth
                            disabled
                            sx={{ borderRadius: "10px", fontSize: "0.78rem", fontWeight: 600, color: "warning.main", borderColor: "warning.main" }}
                          >
                            Verification Pending
                          </Button>
                        ) : (
                          <Button
                            startIcon={<FingerprintIcon />}
                            variant="outlined"
                            color="primary"
                            fullWidth
                            sx={{ borderRadius: "10px", fontSize: "0.78rem", fontWeight: 600 }}
                            onClick={() => { setLoading(true); setOpenCardModal(true); }}
                          >
                            Complete Verification
                          </Button>
                        )
                      )}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>

            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                startIcon={<AddIcon />}
                variant="outlined"
                size="small"
                onClick={() => setOpenAddBankModal(true)}
                sx={{
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  borderColor: "divider",
                  color: "text.secondary",
                  borderRadius: "8px",
                  "&:hover": { borderColor: "primary.main", color: "primary.main", bgcolor: "transparent" },
                }}
              >
                Add Another Account
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* Stripe Connect Verification Dialog */}
      {stripeConnectInstance ? (
        <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
          <Dialog
            open={openCardModal}
            onClose={handleModalClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, minHeight: loading ? 300 : "auto" } }}
          >
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontWeight: 700,
                fontSize: "1rem",
                borderBottom: "1px solid",
                borderColor: "divider",
                pb: 2,
              }}
            >
              <AccountBalanceWalletIcon sx={{ fontSize: "1.2rem", color: "primary.main" }} />
              Add Bank Account
              <IconButton
                onClick={handleModalClose}
                sx={{ ml: "auto", color: "text.secondary" }}
                size="small"
                aria-label="Close dialog"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress size={32} />
                </Box>
              )}
              <Box sx={{ display: loading ? "none" : "block" }}>
                <ConnectAccountOnboarding
                  onExit={() => setOpenSnackbar(true)}
                  onStepChange={() => setLoading(false)}
                />
              </Box>
            </DialogContent>
          </Dialog>
        </ConnectComponentsProvider>
      ) : null}

      <AddBankAccountModal
        open={openAddBankModal}
        onClose={() => setOpenAddBankModal(false)}
        onSuccess={handleAddBankSuccess}
        userCountry={profile?.country}
      />

      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbarSeverity}
          onClose={() => setOpenSnackbar(false)}
          sx={{ fontSize: "0.82rem" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
